// src/pages/Feedback.js - COMPLETE WITH ALL FEATURES + DIRECT LINKS
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Star, MessageCircle, X, Send, Filter, Search, 
  TrendingUp, Users, Award, MessageSquare, Heart,
  Share, ExternalLink, ChevronLeft, ChevronRight,
  ThumbsUp, Bookmark, Flag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ============================================================================
// Feedback Carousel Card Component (SAME AS HOME PAGE)
// ============================================================================
const FeedbackCarouselCard = ({ feedback, onSelect, onLikeUpdate }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(feedback.user_liked || false);
  const [likeCount, setLikeCount] = useState(feedback.likes_count || 0);
  const { user } = useAuth();

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) {
      alert('Please login to like feedback');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/feedback/${feedback._id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount(data.likes_count);
        
        if (onLikeUpdate) {
          onLikeUpdate(feedback._id, data.likes_count, data.liked);
        }
      }
    } catch (error) {
      console.error('Error liking feedback:', error);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const feedbackUrl = `${window.location.origin}/feedback/${feedback._id}`;
    
    if (navigator.share) {
      navigator.share({
        title: `Feedback from ${feedback.name}`,
        text: `"${feedback.message.substring(0, 100)}..."`,
        url: feedbackUrl,
      });
    } else {
      navigator.clipboard.writeText(feedbackUrl);
      alert('Feedback link copied to clipboard!');
    }
  };

  return (
    <div
      onClick={() => onSelect(feedback)}
      className="flex-shrink-0 w-80 md:w-96 bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all cursor-pointer border border-gray-200 dark:border-gray-600 hover:border-blue-400 transform hover:scale-105"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Rating */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-5 h-5 ${
                i < feedback.rating
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300 dark:text-gray-600'
              }`}
            />
          ))}
        </div>
        {feedback.replies && feedback.replies.length > 0 && (
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
            <MessageCircle className="w-4 h-4" />
            <span className="text-xs font-semibold">{feedback.replies.length}</span>
          </div>
        )}
      </div>

      {/* Message */}
      <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3 leading-relaxed font-medium">
        &quot;{feedback.message}&quot;
      </p>

      {/* Image */}
      {feedback.image && (
        <img
          src={feedback.image}
          alt="Feedback"
          className="w-full h-32 object-cover rounded-lg mb-4"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}

      {/* Social Actions (NEW - Same as Home Page) */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all ${
              isLiked 
                ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
            }`}
            aria-label="Like this feedback"
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
            <span className="text-xs font-medium">{likeCount}</span>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(feedback);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/30 transition-all"
            aria-label="View comments"
          >
            <MessageCircle className="w-4 h-4" />
            <span className="text-xs font-medium">{feedback.replies?.length || 0}</span>
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/30 transition-all"
            aria-label="Share feedback"
          >
            <Share className="w-4 h-4" />
          </button>
        </div>

        <button className="
          text-blue-600 dark:text-blue-400 
          text-xs hover:underline
          flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity
        ">
          <span>View Details</span>
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>

      {/* User Info */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
            {feedback.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-gray-900 dark:text-white truncate">{feedback.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {feedback.city && feedback.state ? `${feedback.city}, ${feedback.state}` : feedback.city || 'India'}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{new Date(feedback.createdAt).toLocaleDateString()}</span>
          {feedback.is_pinned && (
            <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
              <ThumbsUp className="w-3 h-3" />
              Featured
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Feedback Page Component
// ============================================================================
export default function Feedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [filterRating, setFilterRating] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [displayCount, setDisplayCount] = useState(12);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const [pinnedFeedbacks, setPinnedFeedbacks] = useState([]);
  const [pinnedIndex, setPinnedIndex] = useState(0);
  const [pinnedPauseAutoRotate, setPinnedPauseAutoRotate] = useState(false);
  const carouselTrackRef = useRef(null);
  const { user } = useAuth();
  const { id } = useParams(); // For direct feedback links
  const navigate = useNavigate();

  const handleCarouselMouseEnter = () => setCarouselPaused(true);
  const handleCarouselMouseLeave = () => setCarouselPaused(false);
  const handleCarouselTouchStart = () => setCarouselPaused(true);
  const handleCarouselTouchEnd = () => setCarouselPaused(false);

  // Function to update like status
  const updateFeedbackLikeStatus = (feedbackId, newLikesCount, newLikedStatus) => {
    setFeedbacks(prev => prev.map(feedback => 
      feedback._id === feedbackId 
        ? { ...feedback, likes_count: newLikesCount, user_liked: newLikedStatus }
        : feedback
    ));

    setPinnedFeedbacks(prev => prev.map(feedback => 
      feedback._id === feedbackId 
        ? { ...feedback, likes_count: newLikesCount, user_liked: newLikedStatus }
        : feedback
    ));

    if (selectedFeedback && selectedFeedback._id === feedbackId) {
      setSelectedFeedback(prev => ({
        ...prev,
        likes_count: newLikesCount,
        user_liked: newLikedStatus
      }));
    }
  };

  useEffect(() => {
    fetchFeedbacks();
    fetchPinnedFeedbacks();
  }, []);

  // Handle direct feedback link
  useEffect(() => {
    if (id && feedbacks.length > 0) {
      const feedback = feedbacks.find(f => f._id === id);
      if (feedback) {
        setSelectedFeedback(feedback);
      } else {
        // If not found in list, fetch it individually
        fetchFeedbackById(id);
      }
    }
  }, [id, feedbacks]);

  const fetchFeedbackById = async (feedbackId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/feedback/${feedbackId}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedFeedback(data.feedback);
        }
      }
    } catch (error) {
      console.error('Error fetching feedback by ID:', error);
    }
  };

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/feedback`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📥 Feedback page - fetched:', data.feedbacks?.length || 0);
        setFeedbacks(data.feedbacks || []);
      } else {
        console.error('Failed to fetch feedbacks:', response.status);
      }
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPinnedFeedbacks = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/feedback/pinned`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📌 Fetched pinned feedbacks:', data.pinnedFeedbacks?.length || 0);
        setPinnedFeedbacks(data.pinnedFeedbacks || []);
      }
    } catch (error) {
      console.error('Error fetching pinned feedbacks:', error);
    }
  };

  useEffect(() => {
    filterFeedbacks();
  }, [feedbacks, filterRating, searchQuery]);

  const filterFeedbacks = () => {
    let filtered = [...feedbacks];
    if (filterRating !== 'all') {
      filtered = filtered.filter(f => f.rating === parseInt(filterRating));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f =>
        f.name.toLowerCase().includes(query) ||
        f.message.toLowerCase().includes(query) ||
        f.city?.toLowerCase().includes(query) ||
        f.state?.toLowerCase().includes(query)
      );
    }
    setFilteredFeedbacks(filtered);
  };

  // Handle modal like
  const handleModalLike = async () => {
    if (!user) {
      alert('Please login to like feedback');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/feedback/${selectedFeedback._id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        updateFeedbackLikeStatus(selectedFeedback._id, data.likes_count, data.liked);
      }
    } catch (error) {
      console.error('Error liking feedback:', error);
    }
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) {
      alert('Please enter a reply message');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/feedback/${selectedFeedback._id}/reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
          body: JSON.stringify({ message: replyText }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to post reply');
        return;
      }

      setReplyText('');
      fetchFeedbacks(); // Refresh the list
      fetchFeedbackById(selectedFeedback._id); // Refresh selected feedback
    } catch (error) {
      console.error('Error posting reply:', error);
      alert('Failed to post reply. Please try again.');
    }
  };

  const stats = useMemo(() => {
    if (feedbacks.length === 0) return { average: 0, distribution: {} };
    const total = feedbacks.reduce((sum, f) => sum + f.rating, 0);
    const average = (total / feedbacks.length).toFixed(1);
    const distribution = {
      5: feedbacks.filter(f => f.rating === 5).length,
      4: feedbacks.filter(f => f.rating === 4).length,
      3: feedbacks.filter(f => f.rating === 3).length,
      2: feedbacks.filter(f => f.rating === 2).length,
      1: feedbacks.filter(f => f.rating === 1).length,
    };
    return { average, distribution };
  }, [feedbacks]);

  const displayedFeedbacks = useMemo(() => {
    return filteredFeedbacks.slice(0, displayCount);
  }, [filteredFeedbacks, displayCount]);

  // Auto-rotate pinned feedbacks
  useEffect(() => {
    if (pinnedFeedbacks.length <= 1 || pinnedPauseAutoRotate) return;

    const interval = setInterval(() => {
      setPinnedIndex((prev) => (prev === pinnedFeedbacks.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [pinnedFeedbacks.length, pinnedPauseAutoRotate]);

  const handlePinnedPrev = () => {
    setPinnedIndex((prev) => (prev === 0 ? pinnedFeedbacks.length - 1 : prev - 1));
    setPinnedPauseAutoRotate(true);
    setTimeout(() => setPinnedPauseAutoRotate(false), 500);
  };

  const handlePinnedNext = () => {
    setPinnedIndex((prev) => (prev === pinnedFeedbacks.length - 1 ? 0 : prev + 1));
    setPinnedPauseAutoRotate(true);
    setTimeout(() => setPinnedPauseAutoRotate(false), 500);
  };

  // Copy direct link to clipboard
  const copyDirectLink = () => {
    if (selectedFeedback) {
      const link = `${window.location.origin}/feedback/${selectedFeedback._id}`;
      navigator.clipboard.writeText(link);
      alert('Direct link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button for Direct Links */}
        {id && (
          <button
            onClick={() => {
              navigate('/feedback');
              setSelectedFeedback(null);
            }}
            className="mb-6 flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to All Feedback
          </button>
        )}

        {/* HERO HEADER */}
        <div className="mb-12 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-2xl p-8 md:p-12 text-white shadow-2xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                Customer Feedback
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                Real experiences from our valued clients. Your trust drives our excellence and continuous improvement.
              </p>
              <div className="flex items-center gap-6 bg-white/10 backdrop-blur-sm p-4 rounded-lg w-fit">
                <div className="flex items-center gap-2">
                  <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                  <span className="text-4xl font-bold">{stats.average}</span>
                </div>
                <div className="text-blue-100 text-lg">
                  Based on <strong>{feedbacks.length}</strong> verified reviews
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=800&h=600&fit=crop"
                alt="Customer feedback"
                className="rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* PINNED FEEDBACK SECTION (NEW - Same as Home Page) */}
        {pinnedFeedbacks.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="h-0.5 sm:h-1 w-4 sm:w-6 md:w-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded"></div>
              <h3 className="text-xl sm:text-2xl font-bold text-center text-gray-900 dark:text-white">
                ⭐ Featured Feedback
              </h3>
              <div className="h-0.5 sm:h-1 w-4 sm:w-6 md:w-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded"></div>
            </div>

            <div 
              className="relative rounded-xl bg-white dark:bg-gray-700 p-4 shadow-xl border-2 border-blue-200 dark:border-blue-900 overflow-hidden"
              onMouseEnter={() => setPinnedPauseAutoRotate(true)}
              onMouseLeave={() => setPinnedPauseAutoRotate(false)}
            >
              <div className="relative z-10">
                <div className="flex-1 flex justify-center">
                  {pinnedFeedbacks[pinnedIndex] && (
                    <div
                      onClick={() => setSelectedFeedback(pinnedFeedbacks[pinnedIndex])}
                      className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                    >
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i}
                            className={`w-5 h-5 ${
                              i < pinnedFeedbacks[pinnedIndex].rating 
                                ? 'text-yellow-400 fill-yellow-400' 
                                : 'text-gray-300 dark:text-gray-600'
                            }`} 
                          />
                        ))}
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3">
                        &quot;{pinnedFeedbacks[pinnedIndex].message}&quot;
                      </p>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!user) {
                              alert('Please login to like feedback');
                              return;
                            }
                            try {
                              const response = await fetch(`${API_URL}/api/feedback/${pinnedFeedbacks[pinnedIndex]._id}/like`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                },
                              });
                              if (response.ok) {
                                const data = await response.json();
                                updateFeedbackLikeStatus(pinnedFeedbacks[pinnedIndex]._id, data.likes_count, data.liked);
                              }
                            } catch (error) {
                              console.error('Error liking feedback:', error);
                            }
                          }}
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all text-sm ${
                            pinnedFeedbacks[pinnedIndex].user_liked
                              ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${pinnedFeedbacks[pinnedIndex].user_liked ? 'fill-red-500 text-red-500' : ''}`} />
                          <span>{pinnedFeedbacks[pinnedIndex].likes_count || 0}</span>
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFeedback(pinnedFeedbacks[pinnedIndex]);
                          }}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/30 text-sm"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>{pinnedFeedbacks[pinnedIndex].replies?.length || 0}</span>
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {pinnedFeedbacks[pinnedIndex].name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(pinnedFeedbacks[pinnedIndex].createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4 text-blue-500" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            Featured
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Progress indicator */}
                <div className="flex justify-center items-center gap-2 mt-4">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {pinnedIndex + 1} / {pinnedFeedbacks.length}
                  </span>
                  <div className="flex gap-1.5">
                    {pinnedFeedbacks.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setPinnedIndex(idx);
                          setPinnedPauseAutoRotate(true);
                          setTimeout(() => setPinnedPauseAutoRotate(false), 500);
                        }}
                        className={`
                          h-2 rounded-full transition-all duration-300 cursor-pointer
                          ${idx === pinnedIndex
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 w-5'
                            : 'bg-gray-300 dark:bg-gray-600 w-2 hover:bg-gray-400'
                          }
                        `}
                        aria-label={`Go to feedback ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              {pinnedFeedbacks.length > 1 && (
                <>
                  <button
                    onClick={handlePinnedPrev}
                    aria-label="Previous feedback"
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 rounded-full bg-blue-600 text-white w-8 h-8 flex items-center justify-center hover:bg-blue-700 hover:scale-110 active:scale-95 shadow-md transition-all duration-200 font-bold"
                  >
                    ‹
                  </button>

                  <button
                    onClick={handlePinnedNext}
                    aria-label="Next feedback"
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 rounded-full bg-blue-600 text-white w-8 h-8 flex items-center justify-center hover:bg-blue-700 hover:scale-110 active:scale-95 shadow-md transition-all duration-200 font-bold"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            
            <div className="text-center mt-3">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Click on any feedback to view details, like, comment, or share
              </p>
            </div>
          </section>
        )}

        {/* STATS SECTION */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 mb-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Average Rating */}
            <div className="text-center">
              <div className="inline-block p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl mb-6 border border-blue-200 dark:border-blue-800">
                <div className="text-6xl font-bold text-blue-600 dark:text-blue-400 mb-3">
                  {stats.average}
                </div>
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-7 h-7 ${
                        i < Math.round(stats.average)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-semibold">
                  Overall Rating
                </p>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Based on <strong className="text-blue-600 dark:text-blue-400">{feedbacks.length}</strong> verified reviews
              </p>
            </div>

            {/* Rating Distribution */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                Rating Distribution
              </h3>
              {[5,4,3,2,1].map((rating) => {
                const count = stats.distribution[rating] || 0;
                const percentage = feedbacks.length > 0 ? ((count / feedbacks.length) * 100).toFixed(0) : 0;
                return (
                  <div key={rating} className="flex items-center gap-4">
                    <div className="flex items-center gap-1 w-24">
                      <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{rating}</span>
                      <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    </div>
                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-400 h-4 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }}></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-20 text-right">{count} ({percentage}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* QUICK STATS CARDS */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 text-center hover:shadow-lg transition-shadow">
            <TrendingUp className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{stats.distribution[5] || 0}</div>
            <div className="text-gray-600 dark:text-gray-400 font-medium">5-Star Reviews</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 text-center hover:shadow-lg transition-shadow">
            <Users className="w-12 h-12 text-blue-600 mx-auto mb-3" />
            <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{feedbacks.length}</div>
            <div className="text-gray-600 dark:text-gray-400 font-medium">Total Reviews</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 text-center hover:shadow-lg transition-shadow">
            <Award className="w-12 h-12 text-purple-600 mx-auto mb-3" />
            <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{((stats.distribution[5] || 0) / (feedbacks.length || 1) * 100).toFixed(0)}%</div>
            <div className="text-gray-600 dark:text-gray-400 font-medium">Satisfaction Rate</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 text-center hover:shadow-lg transition-shadow">
            <MessageCircle className="w-12 h-12 text-orange-600 mx-auto mb-3" />
            <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {feedbacks.reduce((total, f) => total + (f.replies?.length || 0), 0)}
            </div>
            <div className="text-gray-600 dark:text-gray-400 font-medium">Total Replies</div>
          </div>
        </div>

        {/* FILTERS */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md border border-gray-200 dark:border-gray-700 mb-8">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"/>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, message, or location..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-700 dark:text-white"
                aria-label="Search reviews"
              />
            </div>
            {/* Rating Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"/>
              <select
                value={filterRating}
                onChange={(e) => setFilterRating(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-700 dark:text-white appearance-none"
                aria-label="Filter by rating"
              >
                <option value="all">All Ratings</option>
                <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
                <option value="4">⭐⭐⭐⭐ 4 Stars</option>
                <option value="3">⭐⭐⭐ 3 Stars</option>
                <option value="2">⭐⭐ 2 Stars</option>
                <option value="1">⭐ 1 Star</option>
              </select>
            </div>
          </div>

          {(filterRating !== 'all' || searchQuery) && (
            <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Showing <strong>{filteredFeedbacks.length}</strong> of <strong>{feedbacks.length}</strong> reviews
              </span>
              <button
                onClick={() => {
                  setFilterRating('all');
                  setSearchQuery('');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 font-semibold"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* CAROUSEL SECTION */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-6"></div>
              <p className="text-lg text-gray-600 dark:text-gray-400">Loading feedback...</p>
            </div>
          </div>
        ) : filteredFeedbacks.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-block p-12 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
              <MessageSquare className="w-20 h-20 text-gray-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {searchQuery || filterRating !== 'all' ? 'No Matching Reviews' : 'No Feedback Yet'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-sm">
                {searchQuery || filterRating !== 'all'
                  ? 'Try adjusting your filters to see more reviews'
                  : 'Be the first to share your experience with us!'}
              </p>
              <Link
                to="/signup"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition shadow-lg font-semibold"
              >
                Share Your Feedback
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="relative mb-12">
              <div
                className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-8 shadow-xl border border-gray-200 dark:border-gray-700"
                onMouseEnter={handleCarouselMouseEnter}
                onMouseLeave={handleCarouselMouseLeave}
                onTouchStart={handleCarouselTouchStart}
                onTouchEnd={handleCarouselTouchEnd}
              >
                <div className="relative overflow-hidden">
                  <div
                    ref={carouselTrackRef}
                    className="carousel-track-home flex gap-6"
                    style={{
                      animation: 'carousel-scroll 7s linear infinite',
                      animationPlayState: carouselPaused ? 'paused' : 'running',
                    }}
                  >
                    {/* Original feedback cards */}
                    {filteredFeedbacks.map((feedback) => (
                      <FeedbackCarouselCard
                        key={`original-${feedback._id}`}
                        feedback={feedback}
                        onSelect={setSelectedFeedback}
                        onLikeUpdate={updateFeedbackLikeStatus}
                      />
                    ))}

                    {/* Duplicate for seamless loop */}
                    {filteredFeedbacks.length > 1 &&
                      filteredFeedbacks.map((feedback) => (
                        <FeedbackCarouselCard
                          key={`duplicate-${feedback._id}`}
                          feedback={feedback}
                          onSelect={setSelectedFeedback}
                          onLikeUpdate={updateFeedbackLikeStatus}
                        />
                      ))}
                  </div>
                </div>

                {/* GRADIENT FADES */}
                <div className="absolute left-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-r from-white dark:from-gray-800 to-transparent pointer-events-none z-10"></div>
                <div className="absolute right-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-l from-white dark:from-gray-800 to-transparent pointer-events-none z-10"></div>

                {/* PAUSE INDICATOR */}
                {carouselPaused && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold z-20 animate-pulse">
                    ✓ Paused
                  </div>
                )}
              </div>

              <p className="text-center text-gray-600 dark:text-gray-400 text-sm mt-4">
                💡 <span className="hidden md:inline">Hover or </span>Touch to pause •{' '}
                <span className="hidden md:inline">Move away to resume</span>
              </p>
            </div>

            {/* GRID VIEW */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Browse All Reviews</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {displayedFeedbacks.map((feedback) => (
                  <button
                    key={feedback._id}
                    onClick={() => setSelectedFeedback(feedback)}
                    className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-2xl transition-all border border-gray-200 dark:border-gray-700 transform hover:-translate-y-2 text-left w-full"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${
                              i < feedback.rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300 dark:text-gray-600'
                            }`}
                          />
                        ))}
                      </div>
                      {feedback.replies && feedback.replies.length > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                          <MessageCircle className="w-4 h-4" />
                          <span className="text-xs font-semibold">{feedback.replies.length}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 mb-4 line-clamp-3 leading-relaxed">
                      &quot;{feedback.message}&quot;
                    </p>
                    {feedback.image && (
                      <img
                        src={feedback.image}
                        alt="Feedback"
                        className="w-full h-40 object-cover rounded-lg mb-4"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {feedback.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 dark:text-white truncate">{feedback.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {feedback.city && feedback.state ? `${feedback.city}, ${feedback.state}` : feedback.city || 'India'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{new Date(feedback.createdAt).toLocaleDateString()}</span>
                        <span className="text-blue-600 dark:text-blue-400 font-semibold">View →</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {displayedFeedbacks.length < filteredFeedbacks.length && (
                <div className="text-center mb-12">
                  <button
                    onClick={() => setDisplayCount(displayCount + 6)}
                    className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-lg font-semibold inline-block"
                  >
                    Load More Reviews
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* CTA SECTION */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-center text-white shadow-2xl border border-blue-400/30">
          <MessageSquare className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Share Your Experience</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Your feedback helps us improve and serves others in making informed decisions about our services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="inline-block bg-white text-blue-600 px-10 py-4 rounded-lg font-bold hover:bg-gray-100 transition shadow-lg text-lg">
              Submit Your Feedback
            </Link>
            <Link to="/" className="inline-block border-2 border-white text-white px-10 py-4 rounded-lg font-bold hover:bg-white/10 transition shadow-lg text-lg">
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* =========================================================== */}
      {/* FEEDBACK DETAILS MODAL (ENHANCED) */}
      {/* =========================================================== */}
      {selectedFeedback && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={() => {
            setSelectedFeedback(null);
            if (id) navigate('/feedback'); // Go back to feedback list if came from direct link
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-3xl w-full my-8 shadow-2xl border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
            {/* Header with social actions */}
            <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10 rounded-t-2xl">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Feedback Details</h3>
              </div>
              <div className="flex items-center gap-2">
                {/* Social Actions */}
                <button
                  onClick={handleModalLike}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  title="Like"
                >
                  <Heart className={`w-5 h-5 ${selectedFeedback.user_liked ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
                  <span className="text-sm font-medium">{selectedFeedback.likes_count || 0}</span>
                </button>
                
                <button
                  onClick={copyDirectLink}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  title="Copy direct link"
                >
                  <ExternalLink className="w-5 h-5 text-gray-500" />
                </button>
                
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  aria-label="Close modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-6">
              {/* Rating & User Info */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg flex-shrink-0">
                    {selectedFeedback.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-xl text-gray-900 dark:text-white">{selectedFeedback.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{selectedFeedback.email}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedFeedback.city && selectedFeedback.state
                        ? `${selectedFeedback.city}, ${selectedFeedback.state}`
                        : selectedFeedback.city || 'India'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(selectedFeedback.createdAt).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-7 h-7 ${i < selectedFeedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed text-lg">
                  &quot;{selectedFeedback.message}&quot;
                </p>
              </div>

              {/* Image */}
              {selectedFeedback.image && (
                <img
                  src={selectedFeedback.image}
                  alt="Feedback"
                  className="w-full rounded-xl shadow-lg"
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleModalLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    selectedFeedback.user_liked
                      ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${selectedFeedback.user_liked ? 'fill-red-500 text-red-500' : ''}`} />
                  <span className="font-medium">{selectedFeedback.user_liked ? 'Liked' : 'Like'}</span>
                  <span className="text-sm bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full">
                    {selectedFeedback.likes_count || 0}
                  </span>
                </button>
                
                <button
                  onClick={copyDirectLink}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/30 transition"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span className="font-medium">Copy Link</span>
                </button>
              </div>

              {/* Replies Section */}
              <div className="border-t dark:border-gray-700 pt-6">
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <MessageCircle className="w-6 h-6 text-blue-600" />
                  Replies ({selectedFeedback.replies?.length || 0})
                </h4>
                {/* Existing Replies */}
                {selectedFeedback.replies && selectedFeedback.replies.length > 0 ? (
                  <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
                    {selectedFeedback.replies.map((reply, idx) => (
                      <div key={idx} className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4 border border-blue-100 dark:border-gray-600">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">{reply.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(reply.createdAt).toLocaleString()}</p>
                          </div>
                          {/* Edit/Delete buttons for reply owner */}
                          {user && (user.id === reply.userId || user.role === 'admin') && (
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  const newMessage = prompt('Edit your reply:', reply.message);
                                  if (newMessage && newMessage.trim()) {
                                    try {
                                      const response = await fetch(
                                        `${API_URL}/api/feedback/${selectedFeedback._id}/reply/${reply._id}`,
                                        {
                                          method: 'PATCH',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                          },
                                          body: JSON.stringify({ message: newMessage }),
                                        }
                                      );
                                      if (response.ok) {
                                        fetchFeedbackById(selectedFeedback._id);
                                      }
                                    } catch (error) {
                                      console.error('Error editing reply:', error);
                                    }
                                  }
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this reply?')) {
                                    try {
                                      const response = await fetch(
                                        `${API_URL}/api/feedback/${selectedFeedback._id}/reply/${reply._id}`,
                                        {
                                          method: 'DELETE',
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                          },
                                        }
                                      );
                                      if (response.ok) {
                                        fetchFeedbackById(selectedFeedback._id);
                                      }
                                    } catch (error) {
                                      console.error('Error deleting reply:', error);
                                    }
                                  }
                                }}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-center text-gray-600 dark:text-gray-400">
                    <p className="text-sm">No replies yet. Be the first to respond!</p>
                  </div>
                )}

                {/* Add Reply Form */}
                <div className="space-y-4 bg-gray-50 dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                  <h5 className="font-bold text-lg text-gray-900 dark:text-white">Add Your Reply</h5>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write your reply..."
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white resize-none"
                    aria-label="Reply message"
                  />
                  <button
                    onClick={handleReplySubmit}
                    className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition shadow-lg font-semibold text-lg"
                  >
                    <Send className="w-5 h-5" />
                    <span>Post Reply</span>
                  </button>
                </div>
              </div>

              {/* Edit/Delete Feedback for Owner */}
              {user && (user.id === selectedFeedback.userId || user.role === 'admin') && (
                <div className="border-t dark:border-gray-700 pt-6">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Manage Feedback</h4>
                  <div className="flex gap-3">
                    <button
                      onClick={async () => {
                        const newMessage = prompt('Edit your feedback:', selectedFeedback.message);
                        if (newMessage && newMessage.trim()) {
                          try {
                            const response = await fetch(
                              `${API_URL}/api/feedback/my/${selectedFeedback._id}`,
                              {
                                method: 'PATCH',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                },
                                body: JSON.stringify({ message: newMessage }),
                              }
                            );
                            if (response.ok) {
                              const data = await response.json();
                              setSelectedFeedback(data.feedback);
                              alert('Feedback updated successfully!');
                            }
                          } catch (error) {
                            console.error('Error editing feedback:', error);
                          }
                        }
                      }}
                      className="flex-1 bg-yellow-500 text-white px-4 py-3 rounded-lg hover:bg-yellow-600 transition font-semibold"
                    >
                      Edit Feedback
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
                          try {
                            const response = await fetch(
                              `${API_URL}/api/feedback/my/${selectedFeedback._id}`,
                              {
                                method: 'DELETE',
                                headers: {
                                  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                },
                              }
                            );
                            if (response.ok) {
                              setSelectedFeedback(null);
                              alert('Feedback deleted successfully!');
                              fetchFeedbacks();
                              fetchPinnedFeedbacks();
                            }
                          } catch (error) {
                            console.error('Error deleting feedback:', error);
                          }
                        }
                      }}
                      className="flex-1 bg-red-500 text-white px-4 py-3 rounded-lg hover:bg-red-600 transition font-semibold"
                    >
                      Delete Feedback
                    </button>
                  </div>
                </div>
              )}

              {/* Share Section */}
              <div className="border-t dark:border-gray-700 pt-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CAROUSEL ANIMATION STYLES */}
      <style jsx>{`
        @keyframes carousel-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
}